import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Hub from "./components/Hub";
import {
  Snackbar,
  Container,
  CircularProgress,
  Typography,
  Alert,
  Box,
  useMediaQuery,
  Fade,
} from "@mui/material";
import LoginForm from "./components/LoginForm";
import RegistrationForm from "./components/RegistrationForm";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import loadingPhrases from "./utils/LoadingPhrases";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import LockIcon from "@mui/icons-material/Lock";
import PeopleIcon from "@mui/icons-material/People";
import SyncIcon from "@mui/icons-material/Sync";
import ChatIcon from "@mui/icons-material/Chat";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import "./App.css";
import Typewriter from "typewriter-effect";
import useVersionCheck from "./utils/useVersionCheck";
import GitLogs from "./components/GitLogs";
import MediaTable from "./components/MediaTable";
import { videos } from "./components/media/j_theceo/videos";
import { Analytics } from "@vercel/analytics/react";
import VideoGallery from "./components/VideoGallery";
import SingleVideoPlayer from "./components/media/j_theceo/SingleVideoPlayer";
import useLastActiveTracker from "./hooks/useLastActiveTracker";
import PrivacyPolicy from "./components/PrivacyPolicy";

function App() {
  const { versionMessage, open, setOpen } = useVersionCheck();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInteracted, setUserInteracted] = useState(false);
  useLastActiveTracker();

  const stepIcons = [
    WbSunnyIcon,
    LockIcon,
    PeopleIcon,
    SyncIcon,
    ChatIcon,
    AssessmentIcon,
    RocketLaunchIcon,
  ];

  const isVideoRoute =
    location.pathname === "/videos" ||
    /^\/videos\/[^/]+$/.test(location.pathname);

  const skipLoading = location.pathname === "/privacy";

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isDesktop = useMediaQuery("(max-width: 1200px)");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchData = async () => {
      try {
        await Promise.all([fetchUsers(token), fetchCurrentUser(token)]);
      } catch (err) {
        setError(err.message);
      }
    };

    let interval = null;
    if (authenticated) {
      fetchData();
      interval = setInterval(() => {
        fetchData();
      }, 240000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [authenticated]);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/current-user`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch current user");
      }

      const result = await response.json();
      setCurrentUser(result.user);
    } catch (err) {
      console.error("Error fetching current user:", err);
      if (err.message.includes("Unauthorized")) {
        localStorage.removeItem("token");
        setAuthenticated(false);
        setCurrentUser(null);
      }
    }
  };

  useEffect(() => {
    const autoLogin = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setTimeout(() => setLoading(false), 5000);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/verify-token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          setAuthenticated(true);
          await fetchAllData(token);
        } else {
          localStorage.removeItem("token");
          setLoading(false);
        }
      } catch (err) {
        localStorage.removeItem("token");
        setLoading(false);
      }
    };

    autoLogin();
  }, []);

  useEffect(() => {
    const enableAudio = () => {
      setUserInteracted(true);

      document.removeEventListener("pointerdown", enableAudio, {
        capture: true,
      });
      document.removeEventListener("focus", enableAudio, { capture: true });
      document.removeEventListener("keydown", enableAudio, { capture: true });
    };

    document.addEventListener("pointerdown", enableAudio, { capture: true });
    document.addEventListener("focus", enableAudio, { capture: true });
    document.addEventListener("keydown", enableAudio, { capture: true });

    return () => {
      document.removeEventListener("pointerdown", enableAudio, {
        capture: true,
      });
      document.removeEventListener("focus", enableAudio, { capture: true });
      document.removeEventListener("keydown", enableAudio, { capture: true });
    };
  }, []);

  const updateSettings = (newSettings) => {
    if (currentUser) {
      setCurrentUser((prevUser) => ({
        ...prevUser,
        settings: newSettings,
      }));
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowLogin(true);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const randomStart =
      loadingPhrases.start_phrases && loadingPhrases.start_phrases.length > 0
        ? loadingPhrases.start_phrases[
        Math.floor(Math.random() * loadingPhrases.start_phrases.length)
        ]
        : "";
    const randomEnd =
      loadingPhrases.end_phrases && loadingPhrases.end_phrases.length > 0
        ? loadingPhrases.end_phrases[
        Math.floor(Math.random() * loadingPhrases.end_phrases.length)
        ]
        : "";

    const steps = [
      randomStart,
      "Authenticating user credentials...",
      "Aggregating follower data...",
      "Establishing relational contact mapping...",
      "Retrieving appointment and conversation logs...",
      "Compiling real-time performance metrics...",
      randomEnd,
    ];

    setLoadingSteps(steps);

    const simulateLoading = async () => {
      for (let i = 0; i < steps.length; i++) {
        const delay = i === steps.length - 1 ? 450 : 450;
        await new Promise((resolve) => setTimeout(resolve, delay));
        setCurrentStep(i);
      }
      setLoading(false);
    };

    if (authenticated) {
      simulateLoading();
    }
  }, [authenticated]);

  const fetchAllData = async (token) => {
    try {
      await Promise.all([fetchUsers(token)]);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    let interval = null;
    if (authenticated) {
      interval = setInterval(() => {
        fetchAllData(token, { data: false, followers: false, general: false });
      }, 240000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [authenticated]);

  const fetchUsers = async (token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const result = await response.json();

      setUsers(result.users);
    } catch (err) {
      setError(err.message);
    }
  };

  const sampleVideos = [
    {
      id: 1,
      title: "Welcome to Yiggy",
      description: "A quick introduction to Yiggy.",
      url: "https://pouch.jumpshare.com/preview/68vvzk45M-6v5riVPAjQGFfkSbwB-p_42QWcGhKPuCFgDGR6twmWyUStLTCGp51L4su6JlARDfGjoy15uF7kaVxwK4QY2BbWM90P23Z49Hc",
      thumbnail:
        "https://media.discordapp.net/attachments/929500647563346020/1371531665091854356/image.png?ex=682379e6&is=68222866&hm=2689b09f0fa3c1cc3f8692cc86edb0efdb87a2c973ddd873d7499a0da58dae87&=&format=webp&quality=lossless&width=505&height=435",
      duration: 8,
    },
    {
      id: 2,
      title: "How to Get Started",
      description: "Step-by-step guide for new users.",
      url: "https://www.w3schools.com/html/movie.mp4",
      thumbnail: "https://i.imgur.com/1bX5QH6.jpg",
      duration: 12,
    },
    {
      id: 3,
      title: "Advanced Tips",
      description: "Take your productivity to the next level.",
      url: "https://www.w3schools.com/html/mov_bbb.mp4",
      thumbnail: "https://i.imgur.com/8Km9tLL.jpg",
      duration: 10,
    },
  ];

  const handleLogin = async (token) => {
    localStorage.setItem("token", token);
    setAuthenticated(true);
    setLoading(true);
    await fetchAllData(token);
  };

  if (
    loading &&
    currentStep < loadingSteps.length &&
    !isVideoRoute &&
    !skipLoading
  ) {
    const progress = (currentStep / (loadingSteps.length - 1)) * 100;
    const Icon = stepIcons[currentStep];

    return (
      <Fade in timeout={1000}>
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#1a1a1a",
            color: "#98FF98",
          }}
        >
          <Fade in key={currentStep} timeout={1000}>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              style={{
                color: "#98FF98",
                marginBottom: "2rem",
                textAlign: "center",
                textShadow: "1px 1px 8px rgba(152, 255, 152, 0.1)",
              }}
            >
              <Typewriter
                options={{
                  strings: [loadingSteps[currentStep]],
                  autoStart: true,
                  loop: false,
                  delay: 3,
                }}
              />
            </Typography>
          </Fade>

          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={240}
              thickness={7}
              style={{
                color: "rgba(65, 65, 65, 0.3)",
                position: "absolute",
                boxShadow:
                  "0px 4px 15px rgba(30, 30, 30, 0.6), inset 0px 2px 6px rgba(115, 115, 115, 0.3)",
                borderRadius: "50%",
              }}
            />
            <CircularProgress
              variant="determinate"
              value={progress}
              size={240}
              thickness={7}
              style={{
                color: "rgba(152, 255, 152, 0.3)",
                position: "absolute",
                boxShadow:
                  "0px 4px 15px rgba(30, 30, 30, 0.6), inset 0px 2px 6px rgba(115, 115, 115, 0.3)",
                borderRadius: "50%",
              }}
            />
            <CircularProgress
              size={240}
              thickness={1}
              style={{
                color: "rgba(152, 255, 152, 0.8)",
                animationDuration: "1.5s",
                boxShadow:
                  "0px 4px 15px rgba(30, 30, 30, 0.6), inset 0px 2px 6px rgba(115, 115, 115, 0.3)",
                borderRadius: "50%",
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Fade in key={currentStep} timeout={1000}>
                <Icon style={{ color: "#98FF98", fontSize: "3rem" }} />
              </Fade>
            </Box>
          </Box>
        </Container>
      </Fade>
    );
  }

  return (
    <Box>
      <Analytics />
      {open && (
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: "#1a1a1a",
              color: "#98FF98",
              boxShadow:
                "0px 4px 15px rgba(30, 30, 30, 0.6), inset 0px 2px 6px rgba(115, 115, 115, 0.3)",
              borderRadius: "8px",
              padding: "12px 16px",
              textAlign: "center",
            },
          }}
          message={
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {versionMessage}
              </Typography>
            </Box>
          }
        />
      )}
      <Routes>
        <Route path="/videos/:videoId" element={<SingleVideoPlayer />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/videos"
          element={
            !authenticated ? (
              <Container
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                {showLogin ? (
                  <Fade in timeout={1000}>
                    <Box>
                      {showRegister ? (
                        <RegistrationForm
                          onRegisterSuccess={() => setShowRegister(false)}
                          onSwitchToLogin={() => setShowRegister(false)}
                        />
                      ) : (
                        <LoginForm
                          onLogin={handleLogin}
                          onSwitchToRegister={() => setShowRegister(true)}
                        />
                      )}
                    </Box>
                  </Fade>
                ) : null}
              </Container>
            ) : (
              <Container sx={{ marginTop: "32px" }}>
                <VideoGallery videos={sampleVideos} />
              </Container>
            )
          }
        />
        <Route
          path="/"
          element={
            !authenticated ? (
              <Container
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                {showLogin ? (
                  <Fade in timeout={1000}>
                    <Box>
                      {showRegister ? (
                        <RegistrationForm
                          onRegisterSuccess={() => setShowRegister(false)}
                          onSwitchToLogin={() => setShowRegister(false)}
                        />
                      ) : (
                        <LoginForm
                          onLogin={handleLogin}
                          onSwitchToRegister={() => setShowRegister(true)}
                        />
                      )}
                    </Box>
                  </Fade>
                ) : null}
              </Container>
            ) : error ? (
              <Container style={{ textAlign: "center", marginTop: "2rem" }}>
                <Typography variant="h6">{error}</Typography>
              </Container>
            ) : (
              <Container>
                <Hub
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  users={users}
                  setUsers={setUsers}
                  updateSettings={updateSettings}
                />
              </Container>
            )
          }
        />
        <Route
          path="/updates"
          element={
            !authenticated ? (
              <Container
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                {showLogin ? (
                  <Fade in timeout={1000}>
                    <Box>
                      {showRegister ? (
                        <RegistrationForm
                          onRegisterSuccess={() => setShowRegister(false)}
                          onSwitchToLogin={() => setShowRegister(false)}
                        />
                      ) : (
                        <LoginForm
                          onLogin={handleLogin}
                          onSwitchToRegister={() => setShowRegister(true)}
                        />
                      )}
                    </Box>
                  </Fade>
                ) : null}
              </Container>
            ) : (
              <GitLogs />
            )
          }
        />

        <Route
          path="/content"
          element={
            !authenticated ? (
              <Container
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                {showLogin ? (
                  <Fade in timeout={1000}>
                    <Box>
                      {showRegister ? (
                        <RegistrationForm
                          onRegisterSuccess={() => setShowRegister(false)}
                          onSwitchToLogin={() => setShowRegister(false)}
                        />
                      ) : (
                        <LoginForm
                          onLogin={handleLogin}
                          onSwitchToRegister={() => setShowRegister(true)}
                        />
                      )}
                    </Box>
                  </Fade>
                ) : null}
              </Container>
            ) : (
              <Container
                sx={{
                  width: isDesktop ? "100%" : "800px",
                  maxWidth: "800px",
                  marginTop: "32px",
                }}
              >
                <MediaTable mediaData={videos} />
              </Container>
            )
          }
        />
      </Routes>
    </Box>
  );
}

export default App;